import { RunningExperiments, useExperiments } from 'state/experiments';
import { useMenuContext } from 'state/menu';
import { env } from 'utils/environment';

/**
 * Hook for the Homepage Hero Image A/B/C experiment
 *
 * @description
 * Tests 3 different homepage hero images for Maple Bourbon Whopper campaign.
 * - Assignment: On app load (handled automatically by Amplitude SDK)
 * - Exposure: Logged manually when:
 *    1. User is on homepage (pathname = /)
 *    2. Daypart is lunch or dinner
 *    3. Experiment data is successfully fetched (simpleHomePage provided)
 * - Variants: control, variant_a, variant_b
 * - Each variant has a payload with sanityId pointing to a SimpleHomePage document
 *
 * @param simpleHomePage - The fetched SimpleHomePage data (optional) used to verify content availability before logging exposure
 * @returns {{ experimentSanityId?: string }} The sanityId from the assigned variant if conditions are met
 */

export const useHomepageHeroExperiment = (): {
  experimentSanityId?: string;
  isLoading: boolean;
} => {
  const { getVariantValue, isLoading } = useExperiments();
  const { activeDayParts } = useMenuContext();

  const isOnLunchOrDinnerTime = activeDayParts.some(
    daypart => daypart?.key && ['lunch', 'main menu'].includes(daypart?.key.toLowerCase())
  );

  const variantData = getVariantValue(RunningExperiments.HomepageHeroImage);
  const sanityId = variantData?.payload?.[env()] ?? variantData?.payload?.sanityId;
  const experimentSanityId = isOnLunchOrDinnerTime && sanityId ? sanityId : undefined;

  return {
    experimentSanityId,
    isLoading,
  };
};
